import { useEffect, useState } from 'react';
import '@/App.css';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, TrendingUp, Factory, Calendar, Award, LogOut, KeyRound, X, Download, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { DateRangePicker } from '@/components/DateRangePicker';
import { MachineGroupCheckboxes } from '@/components/MachineGroupCheckboxes';
import { Login } from '@/components/Login';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';
import { MachineMonthlyTable } from '@/components/MachineMonthlyTable';
import { ExportReportDialog } from '@/components/ExportReportDialog';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert kg to MT
const kgToMT = (kg) => Math.round(kg / 1000);

function App() {
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [last12MonthsData, setLast12MonthsData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [bestMonth, setBestMonth] = useState(null);
  const [machineGroups, setMachineGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState(['all']);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [groupProduction, setGroupProduction] = useState([]);
  const [machineProduction, setMachineProduction] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'primary', 'secondary'
  const [lastSyncTime, setLastSyncTime] = useState(localStorage.getItem('lastSyncTime') || null);

  // Primary production categories (exact names from database)
  const PRIMARY_CATEGORIES = ['Bolt Forging', 'Nut Forming'];

  // Handle category filter change
  const handleCategoryFilter = (category) => {
    setCategoryFilter(category);
    
    let newSelectedGroups;
    if (category === 'all') {
      newSelectedGroups = ['all'];
    } else if (category === 'primary') {
      newSelectedGroups = PRIMARY_CATEGORIES;
    } else {
      // Secondary - all groups except primary
      const secondaryGroups = machineGroups
        .filter(g => !PRIMARY_CATEGORIES.includes(g.name))
        .map(g => g.name);
      newSelectedGroups = secondaryGroups.length > 0 ? secondaryGroups : ['all'];
    }
    
    setSelectedGroups(newSelectedGroups);
    fetchData(newSelectedGroups, dateRange);
  };

  // PWA Install prompt handler
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallButton(false);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }
    setDeferredPrompt(null);
  };

  // Set up axios interceptor for auth token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
    }
    setCheckingAuth(false);

    // Add response interceptor to handle 401 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setIsAuthenticated(false);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const handleLoginSuccess = async () => {
    const token = localStorage.getItem('token');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsAuthenticated(true);
    
    // Auto sync on login
    setTimeout(() => {
      handleSync(true);
    }, 500);
  };

  const fetchData = async (groups = ['all'], dateRangeParam = null) => {
    try {
      const range = dateRangeParam || dateRange;
      const params = {};
      
      if (range?.from) {
        // Format date as YYYY-MM-DD without timezone conversion
        const fromDate = range.from;
        params.start_date = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`;
      }
      if (range?.to) {
        // Format date as YYYY-MM-DD without timezone conversion
        const toDate = range.to;
        params.end_date = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`;
      }
      
      // Handle multiple groups
      if (groups.length > 0 && !groups.includes('all')) {
        params.machine_groups = groups.join(',');
      }

      const [statsRes, monthlyRes, yearlyRes, bestRes, groupsRes, groupProdRes, machineProdRes, last12Res, dailyRes] = await Promise.all([
        axios.get(`${API}/production/stats`, { params }),
        axios.get(`${API}/production/monthly`, { params }),
        axios.get(`${API}/production/yearly`, { params }),
        axios.get(`${API}/production/best-month`, { params }),
        axios.get(`${API}/machine-groups`),
        axios.get(`${API}/production/by-machine-group`, { params }),
        axios.get(`${API}/production/by-machine`, { params }),
        axios.get(`${API}/production/last-12-months`, { params }),
        axios.get(`${API}/production/daily`, { params }),
      ]);

      setStats(statsRes.data);
      
      // Convert monthly data to MT and round
      setMonthlyData(monthlyRes.data.map(item => ({
        ...item,
        total_mt: Math.round(item.total_kg / 1000)
      })));
      
      // Convert yearly data to MT and round
      setYearlyData(yearlyRes.data.map(item => ({
        ...item,
        total_mt: Math.round(item.total_kg / 1000)
      })));

      // Convert last 12 months data to MT
      setLast12MonthsData(last12Res.data.map(item => ({
        ...item,
        total_mt: Math.round(item.total_kg / 1000)
      })));

      // Convert daily data to MT
      setDailyData(dailyRes.data.map(item => ({
        ...item,
        total_mt: Math.round(item.total_kg / 1000)
      })));
      
      setBestMonth(bestRes.data);
      setMachineGroups(groupsRes.data);
      setGroupProduction(groupProdRes.data);
      setMachineProduction(machineProdRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        setLoading(false);
      }
    }
  };

  const handleSync = async (showToast = true) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sync-dropbox`);
      
      // Save last sync time
      const now = new Date().toISOString();
      localStorage.setItem('lastSyncTime', now);
      setLastSyncTime(now);
      
      if (showToast) {
        toast.success(response.data.message, {
          description: `Synced ${response.data.records_synced} records`,
        });
      }
      await fetchData(selectedGroups);
    } catch (error) {
      console.error('Error syncing:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Failed to sync data from Dropbox';
      if (showToast) {
        toast.error('Sync failed', {
          description: errorDetail,
          duration: 10000,
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleGroupChange = (values) => {
    // If "all" is in the new selection, clear everything else
    if (values.includes('all') && !selectedGroups.includes('all')) {
      setSelectedGroups(['all']);
      fetchData(['all'], dateRange);
    }
    // If selecting other groups, remove "all"
    else if (values.length > 0 && values.includes('all')) {
      const withoutAll = values.filter(v => v !== 'all');
      if (withoutAll.length > 0) {
        setSelectedGroups(withoutAll);
        fetchData(withoutAll, dateRange);
      } else {
        setSelectedGroups(['all']);
        fetchData(['all'], dateRange);
      }
    }
    // Normal multi-select without "all"
    else if (values.length > 0) {
      setSelectedGroups(values);
      fetchData(values, dateRange);
    }
    // If nothing selected, default to 'all'
    else {
      setSelectedGroups(['all']);
      fetchData(['all'], dateRange);
    }
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      fetchData(selectedGroups, range);
    } else if (!range?.from && !range?.to) {
      // Clear date range
      fetchData(selectedGroups, { from: undefined, to: undefined });
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Show login if not authenticated
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="App min-h-screen bg-background">
      <div className="noise-texture" />
      
      <div className="relative z-10 p-6 md:p-8 lg:p-12">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <div className="flex items-center gap-6">
              <img 
                src="/simplex-logo.jpeg" 
                alt="Simplex Logo" 
                className="h-16 w-auto object-contain"
                data-testid="company-logo"
              />
              <div className="border-l border-border h-16 hidden md:block" />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
                  Production Dashboard
                </h1>
                <p className="text-muted-foreground text-sm uppercase tracking-widest">
                  REAL-TIME MANUFACTURING ANALYTICS
                </p>
              </div>
            </div>
            
            <div className="flex flex-col items-start md:items-end gap-2">
              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                {showInstallButton && (
                  <Button
                    data-testid="install-app-button"
                    onClick={handleInstallClick}
                    className="rounded-sm uppercase tracking-wide font-semibold shadow-sm bg-green-600 hover:bg-green-700 text-xs md:text-sm"
                    size="sm"
                  >
                    <Download className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
                    Install
                  </Button>
                )}

                <Button
                  data-testid="sync-button"
                  onClick={() => handleSync(true)}
                  disabled={syncing}
                  className="rounded-sm uppercase tracking-wide font-semibold shadow-sm text-xs md:text-sm"
                  size="sm"
                >
                  <RefreshCw className={`mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'SYNCING...' : 'SYNC'}
                </Button>

                <Button
                  onClick={() => setShowExportDialog(true)}
                  variant="outline"
                  className="rounded-sm uppercase tracking-wide font-semibold bg-blue-50 hover:bg-blue-100 border-blue-200 text-xs md:text-sm"
                  size="sm"
                  data-testid="export-report-button"
                >
                  <FileDown className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 text-blue-600" />
                  Export
                </Button>

                <Button
                  onClick={() => setShowChangePassword(true)}
                  variant="outline"
                  className="rounded-sm uppercase tracking-wide font-semibold text-xs md:text-sm"
                  size="sm"
                  data-testid="change-password-button"
                >
                  <KeyRound className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">Password</span>
                  <span className="sm:hidden">PWD</span>
                </Button>

                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="rounded-sm uppercase tracking-wide font-semibold text-xs md:text-sm"
                  size="sm"
                  data-testid="logout-button"
                >
                  <LogOut className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
                  Logout
                </Button>
              </div>
              {lastSyncTime && (
                <p className="text-xs text-muted-foreground" data-testid="last-sync-time">
                  Last synced: {new Date(lastSyncTime).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card data-testid="total-production-card" className="rounded-sm border-border shadow-sm">
                  <CardHeader className="pb-3 border-b border-border">
                    <CardTitle className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                      Total Production
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      <div>
                        <div className="text-3xl font-bold tabular-nums">
                          {formatNumber(kgToMT(stats?.total_production_kg || 0))}
                        </div>
                        <div className="text-sm text-muted-foreground">MT</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="machine-count-card" className="rounded-sm border-border shadow-sm">
                  <CardHeader className="pb-3 border-b border-border">
                    <CardTitle className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                      Active Machines
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Factory className="h-5 w-5 text-primary" />
                      <div>
                        <div className="text-3xl font-bold tabular-nums">
                          {stats?.machine_count || 0}
                        </div>
                        <div className="text-sm text-muted-foreground">machines</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="records-count-card" className="rounded-sm border-border shadow-sm">
                  <CardHeader className="pb-3 border-b border-border">
                    <CardTitle className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                      Total Records
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-primary" />
                      <div>
                        <div className="text-3xl font-bold tabular-nums">
                          {formatNumber(stats?.total_records || 0)}
                        </div>
                        <div className="text-sm text-muted-foreground">entries</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  data-testid="best-month-card"
                  className="rounded-sm border-secondary shadow-sm bg-secondary/5"
                >
                  <CardHeader className="pb-3 border-b border-secondary/20">
                    <CardTitle className="text-xs uppercase tracking-widest text-secondary-foreground font-medium">
                      Best Month
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Award className="h-5 w-5 text-secondary" />
                      <div>
                        <div className="text-2xl font-bold">{bestMonth?.period || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">
                          {bestMonth ? `${formatNumber(kgToMT(bestMonth.total_kg))} MT` : ''}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mb-6">
                {/* Category Filter Buttons */}
                <div className="mb-4">
                  <p className="text-sm uppercase tracking-widest font-semibold mb-3">
                    Production Category:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid="category-all-button"
                      onClick={() => handleCategoryFilter('all')}
                      variant={categoryFilter === 'all' ? 'default' : 'outline'}
                      className="rounded-sm uppercase tracking-wide font-semibold"
                      size="sm"
                    >
                      All Categories
                    </Button>
                    <Button
                      data-testid="category-primary-button"
                      onClick={() => handleCategoryFilter('primary')}
                      variant={categoryFilter === 'primary' ? 'default' : 'outline'}
                      className={`rounded-sm uppercase tracking-wide font-semibold ${categoryFilter === 'primary' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      size="sm"
                    >
                      Primary (Bolt Forging, Nut Forming)
                    </Button>
                    <Button
                      data-testid="category-secondary-button"
                      onClick={() => handleCategoryFilter('secondary')}
                      variant={categoryFilter === 'secondary' ? 'default' : 'outline'}
                      className={`rounded-sm uppercase tracking-wide font-semibold ${categoryFilter === 'secondary' ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
                      size="sm"
                    >
                      Secondary (Others)
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-4 mb-4">
                  <div className="flex-1">
                    <p className="text-sm uppercase tracking-widest font-semibold mb-3">
                      Filter by Machine Group:
                    </p>
                    <MachineGroupCheckboxes
                      groups={machineGroups}
                      selectedGroups={selectedGroups}
                      onGroupChange={handleGroupChange}
                    />
                  </div>
                  
                  <div className="lg:w-80">
                    <p className="text-sm uppercase tracking-widest font-semibold mb-3">
                      Date Range:
                    </p>
                    <DateRangePicker 
                      dateRange={dateRange} 
                      onDateRangeChange={handleDateRangeChange}
                    />
                  </div>

                  <div className="flex items-end">
                    <Button
                      data-testid="clear-filters-button"
                      onClick={() => {
                        setSelectedGroups(['all']);
                        setDateRange({ from: undefined, to: undefined });
                        setCategoryFilter('all');
                        fetchData(['all'], { from: undefined, to: undefined });
                      }}
                      variant="outline"
                      className="rounded-sm uppercase tracking-wide font-semibold"
                      disabled={categoryFilter === 'all' && selectedGroups.includes('all') && selectedGroups.length === 1 && !dateRange?.from && !dateRange?.to}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
                <Card
                  data-testid="monthly-chart-card"
                  className="lg:col-span-8 rounded-sm border-border shadow-sm"
                >
                  <CardHeader className="border-b border-border">
                    <CardTitle className="text-lg font-bold uppercase tracking-tight">
                      Monthly Production Trend
                    </CardTitle>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                      {selectedGroups.includes('all') || selectedGroups.length === 0
                        ? 'All Machine Groups'
                        : selectedGroups.join(', ')}
                    </p>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={monthlyData} margin={{ top: 20, right: 40, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis
                          dataKey="month"
                          stroke="hsl(var(--muted-foreground))"
                          style={{ fontSize: '12px', fontWeight: 500 }}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          style={{ fontSize: '12px', fontWeight: 500 }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '0.125rem',
                            fontSize: '12px',
                          }}
                          formatter={(value) => [new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value) + ' MT', 'Production']}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                        <Line
                          type="monotone"
                          dataKey="total_mt"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                          name="Production (MT)"
                          label={{
                            position: 'top',
                            offset: 10,
                            style: { 
                              fontSize: '12px', 
                              fontWeight: 700,
                              fill: '#000000',
                              textShadow: '1px 1px 2px white, -1px -1px 2px white'
                            },
                            formatter: (value) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 0 }).format(value)
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card
                  data-testid="yearly-chart-card"
                  className="lg:col-span-4 rounded-sm border-border shadow-sm"
                >
                  <CardHeader className="border-b border-border">
                    <CardTitle className="text-lg font-bold uppercase tracking-tight">
                      Yearly Comparison
                    </CardTitle>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                      Total Production by Year
                    </p>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={yearlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis
                          dataKey="year"
                          stroke="hsl(var(--muted-foreground))"
                          style={{ fontSize: '12px', fontWeight: 500 }}
                          angle={0}
                          tickMargin={10}
                          interval={0}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          style={{ fontSize: '12px', fontWeight: 500 }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '0.125rem',
                            fontSize: '12px',
                          }}
                          formatter={(value) => [new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value) + ' MT', 'Production']}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                        <Bar 
                          dataKey="total_mt" 
                          fill="hsl(var(--secondary))" 
                          name="Production (MT)"
                          radius={[4, 4, 0, 0]}
                          label={{
                            position: 'top',
                            offset: 5,
                            style: { 
                              fontSize: '12px', 
                              fontWeight: 700,
                              fill: '#000000'
                            },
                            formatter: (value) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 0 }).format(value)
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Last 12 Months Production Chart */}
              <div className="mt-6">
                <Card
                  data-testid="last-12-months-chart-card"
                  className="rounded-sm border-border shadow-sm"
                >
                  <CardHeader className="border-b border-border">
                    <CardTitle className="text-lg font-bold uppercase tracking-tight">
                      Last 12 Months Production
                    </CardTitle>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                      {selectedGroups.includes('all') || selectedGroups.length === 0
                        ? 'All Machine Groups'
                        : selectedGroups.join(', ')}
                    </p>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={last12MonthsData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis
                          dataKey="month"
                          stroke="hsl(var(--muted-foreground))"
                          style={{ fontSize: '11px', fontWeight: 500 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          style={{ fontSize: '12px', fontWeight: 500 }}
                          tickFormatter={(value) => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value)}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '0.125rem',
                            fontSize: '12px',
                          }}
                          formatter={(value) => [new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value) + ' MT', 'Production']}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                        <Bar 
                          dataKey="total_mt" 
                          fill="hsl(var(--primary))" 
                          name="Production (MT)"
                          radius={[4, 4, 0, 0]}
                          label={{
                            position: 'top',
                            style: { fontSize: '10px', fill: 'hsl(var(--foreground))', fontWeight: 600 },
                            formatter: (value) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 0 }).format(value)
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Production Chart */}
              <div className="mt-6">
                <Card
                  data-testid="daily-chart-card"
                  className="rounded-sm border-border shadow-sm"
                >
                  <CardHeader className="border-b border-border">
                    <CardTitle className="text-lg font-bold uppercase tracking-tight">
                      Daily Production
                    </CardTitle>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                      {dateRange?.from && dateRange?.to 
                        ? `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`
                        : `Current Month (${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })})`}
                      {' • '}
                      {selectedGroups.includes('all') || selectedGroups.length === 0
                        ? 'All Machine Groups'
                        : selectedGroups.join(', ')}
                    </p>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={dailyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis
                          dataKey="day"
                          stroke="hsl(var(--muted-foreground))"
                          style={{ fontSize: '11px', fontWeight: 500 }}
                          tickFormatter={(value) => value}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          style={{ fontSize: '12px', fontWeight: 500 }}
                          tickFormatter={(value) => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value)}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '0.125rem',
                            fontSize: '12px',
                          }}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.date || `Day ${label}`}
                          formatter={(value) => [new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value) + ' MT', 'Production']}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                        <Bar 
                          dataKey="total_mt" 
                          fill="#10b981"
                          name="Daily Production (MT)"
                          radius={[4, 4, 0, 0]}
                          label={{
                            position: 'top',
                            style: { fontSize: '9px', fill: 'hsl(var(--foreground))', fontWeight: 600 },
                            formatter: (value) => value > 0 ? value : ''
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6">
                <Card data-testid="production-table" className="rounded-sm border-border shadow-sm">
                  <CardHeader className="border-b border-border">
                    <CardTitle className="text-lg font-bold uppercase tracking-tight">
                      Production by Machine Group
                    </CardTitle>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                      {dateRange?.from && dateRange?.to 
                        ? `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`
                        : 'All Time'}
                    </p>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                              Machine Group
                            </th>
                            <th className="text-right py-3 px-4 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                              Production (MT)
                            </th>
                            <th className="text-right py-3 px-4 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                              Percentage
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupProduction.map((group, index) => (
                            <tr 
                              key={group.machine_group}
                              className="border-b border-border hover:bg-muted/50 transition-colors"
                              data-testid={`table-row-${group.machine_group.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <td className="py-3 px-4 font-medium">
                                {group.machine_group}
                              </td>
                              <td className="py-3 px-4 text-right tabular-nums font-semibold">
                                {formatNumber(Math.round(group.total_mt))}
                              </td>
                              <td className="py-3 px-4 text-right tabular-nums">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="flex-1 max-w-[100px] bg-muted rounded-full h-2 overflow-hidden">
                                    <div 
                                      className="h-full bg-primary"
                                      style={{ width: `${group.percentage}%` }}
                                    />
                                  </div>
                                  <span className="font-semibold min-w-[45px]">
                                    {group.percentage}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6">
                <Card data-testid="machine-production-table" className="rounded-sm border-border shadow-sm">
                  <CardHeader className="border-b border-border">
                    <CardTitle className="text-lg font-bold uppercase tracking-tight">
                      Production by Machine
                    </CardTitle>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                      {dateRange?.from && dateRange?.to 
                        ? `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`
                        : 'All Time'} • {machineProduction.length} Machines
                    </p>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                              Machine
                            </th>
                            <th className="text-left py-3 px-4 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                              Machine Group
                            </th>
                            <th className="text-right py-3 px-4 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                              Production (MT)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {machineProduction.map((machine, index) => (
                            <tr 
                              key={`${machine.machine}-${index}`}
                              className="border-b border-border hover:bg-muted/50 transition-colors"
                              data-testid={`machine-row-${machine.machine.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <td className="py-3 px-4 font-semibold">
                                {machine.machine}
                              </td>
                              <td className="py-3 px-4 text-muted-foreground text-sm">
                                {machine.machine_group}
                              </td>
                              <td className="py-3 px-4 text-right tabular-nums font-semibold">
                                {formatNumber(Math.round(machine.total_mt))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Machine-wise Monthly Production Table */}
              <div className="mt-6">
                <MachineMonthlyTable 
                  machineGroups={machineGroups}
                  selectedGroups={selectedGroups}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <ChangePasswordDialog 
        open={showChangePassword} 
        onOpenChange={setShowChangePassword}
      />

      <ExportReportDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        dashboardData={{
          stats: stats ? {
            total_production: kgToMT(stats.total_production_kg || 0),
            machine_count: stats.machine_count,
            total_records: stats.total_records
          } : null,
          bestMonth,
          monthlyData,
          yearlyData,
          dailyData,
          last12MonthsData,
          machineGroupData: groupProduction,
          machineData: machineProduction
        }}
        dateRange={dateRange}
        selectedGroups={selectedGroups}
      />
    </div>
  );
}

export default App;